import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProductGroup } from '@app/core/models/client';
import { Language } from '@app/core/models/language.model';
import { Permissions, PermissionService } from '@app/core/services/permission.service';
import { SmsLanguageConfigService } from '@app/core/services/sms-language-config.service';
import * as _ from 'lodash';
import { forkJoin, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { ErrorDisplayComponent } from '../../../../../shared/error-display/error-display.component';

@Component({
    selector: 'app-product-help-text',
    templateUrl: './help-text.component.html',
    styleUrls: ['./help-text.component.scss'],
    standalone: true,
    imports: [NgClass, FormsModule, ReactiveFormsModule, ErrorDisplayComponent]
})
export class HelpTextComponent implements OnInit {

  @Input() productGroup: ProductGroup;
  @Input() clientId: string;
  /** A product group that isn't saved yet has no stored help text to load. */
  @Input() isNew = false;
  /**
   * Help text edited and not yet saved, by language code. The page owns it, so
   * edits survive this section being collapsed, and sends it with its Save.
   * It also holds the old fields' text for English and Spanish when they have
   * no reply yet: see prefillFromLegacy.
   */
  @Input() edits: { [languageCode: string]: string } = {};

  helpTextForm: UntypedFormGroup;
  /**
   * legacy: the two fixed boxes. They show from the start, so a client with
   * multi-language-stop-help off never waits on the flag check, and stay if
   * the flag is off or the check fails. multi: the flag is on. denied: it's
   * on, but the user can't view and edit sms language config.
   */
  mode: 'legacy' | 'multi' | 'denied' = 'legacy';
  loadFailed = false;
  /** The client's Active languages, alphabetical. */
  languages: Language[] = [];
  selectedCode: string;
  private stored: { [languageCode: string]: string } = {};

  constructor(
    private formBuilder: UntypedFormBuilder,
    private smsLanguageConfigService: SmsLanguageConfigService,
    private permissionService: PermissionService,
  ) { }

  get text(): string {
    return this.edits[this.selectedCode] ?? this.stored[this.selectedCode] ?? '';
  }

  get direction(): 'ltr' | 'rtl' {
    return this.languages.find((l) => l.code === this.selectedCode)?.direction === 'RTL' ? 'rtl' : 'ltr';
  }

  ngOnInit() {
    this.helpTextForm = this.formBuilder.group({
      'help_instructions': [this.productGroup.help_instructions, Validators.required],
      'help_instructions_spanish': [this.productGroup.help_instructions_spanish]
    });

    this.helpTextForm.valueChanges.subscribe((val) => _.assign(this.productGroup, val));

    // Short timeout so the flag can't hold up the page.
    this.smsLanguageConfigService
      .isEnabled(this.clientId)
      .pipe(timeout(5000), catchError(() => of(false)))
      .subscribe((enabled) => {
        if (!enabled) {
          return;
        }
        if (
          this.permissionService.checkPermissions(Permissions.sms_language_config_view)
          && this.permissionService.checkPermissions(Permissions.sms_language_config_edit)
        ) {
          this.mode = 'multi';
          this.load();
        } else {
          this.mode = 'denied';
        }
      });
  }

  onTextChange(text: string) {
    this.edits[this.selectedCode] = text;
  }

  /** Cross-references the language library against this client's languages to find the Active ones. */
  private load() {
    forkJoin({
      library: this.smsLanguageConfigService.getLanguageLibrary(this.clientId),
      clientLanguages: this.smsLanguageConfigService.getClientLanguages(this.clientId),
      stored: this.isNew
        ? of({})
        : this.smsLanguageConfigService.getProductGroupHelpReplies(this.clientId, this.productGroup.id),
    }).subscribe({
      next: ({ library, clientLanguages, stored }) => {
        const active = new Set(clientLanguages.filter((l) => l.enabled).map((l) => l.languageCode));
        this.languages = library
          .filter((l) => active.has(l.code))
          .sort((a, b) => a.englishName.localeCompare(b.englishName));
        this.stored = stored;
        this.prefillFromLegacy();
        this.selectedCode = this.languages[0]?.code;
      },
      // Editing without the stored text would overwrite it, so show nothing to edit.
      error: () => (this.loadFailed = true),
    });
  }

  /**
   * An Active English or Spanish with no reply of its own starts from the old
   * help_instructions(_spanish) field, which is what HELP sends today. A new
   * product group's English is the model's default text. It goes in as an
   * edit, so Save stores what's shown. Left in the old field, Spanish text
   * isn't what Spanish customers get once the product group has an English
   * reply: rn-v3 tries that first.
   */
  private prefillFromLegacy() {
    const legacy: { [languageCode: string]: string | undefined } = {
      en: this.productGroup.help_instructions,
      es: this.productGroup.help_instructions_spanish,
    };
    this.languages.forEach(({ code }) => {
      const text = legacy[code];
      if (text?.trim() && this.stored[code] === undefined && this.edits[code] === undefined) {
        this.edits[code] = text;
      }
    });
  }
}
